import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { PurchaseApi } from "src/services/Api/Organization/types";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { selectOrganization } from "go-security/services/organizations/selectors";

interface Props {
	purchase: PurchaseApi;
}

const PurchaseItemsCardTableSummary = ({ purchase }: Props) => {
	const { t } = useTranslation();
	const taxItems = purchase?.tax_items || [];
	const currency = useSelector(selectOrganization).currency || "";
	const sameCurrency = currency === purchase.currency;
	const exchangeRate = purchase.exchange_rate;
	const isActionColumnShown = purchase.items.some((item) => item?.product_id || item?.linked_product?.id);

	return (
		<>
			<tr className="summary">
				<td colSpan={4}>
					<div className="d-flex flex-column">
						<span style={{ marginBottom: "30px" }}>
							<strong>{t("common.word.summary", { ns: "lib" })}</strong>
						</span>
						<span>{t("modules.purchase.field.summary_taxes.title")}</span>
					</div>
				</td>
				<td>
					<span className="label text-nowrap">
						{t("common.word.value")}
						<br /> {t("enums.price.type.NET").toLowerCase()}
					</span>
					{taxItems.map((tax, index) => {
						return (
							<div key={index} className="mb-1 nowrap">
								{FormatMoney(tax.price_net)}
							</div>
						);
					})}
				</td>
				<td>
					<span className="label">
						{t("common.word.vat_rate")}
						<br /> {t("common.word.vat")}
					</span>
					{taxItems.map((tax, index) => {
						return (
							<div key={index} className="mb-1 nowrap">
								{tax.name}
							</div>
						);
					})}
				</td>
				<td className="w-1">
					<span className="label">{t("modules.purchase.field.price_vat.title")}</span>
					{taxItems.map((tax, index) => {
						return (
							<div key={index} className="mb-1 nowrap">
								{FormatMoney(tax.price_tax)}
							</div>
						);
					})}
				</td>
				<td colSpan={isActionColumnShown ? 2 : 1}>
					<span className="label">
						{t("common.word.value")}
						<br /> {t("enums.price.type.GROSS").toLowerCase()}
					</span>
					{taxItems.map((tax, index) => {
						return (
							<div key={index} className="mb-1 nowrap">
								{FormatMoney(tax.price_gross)}
							</div>
						);
					})}
				</td>
			</tr>
			<tr>
				<td className={`nowrap nested-cell ${!sameCurrency ? "pb-1" : ""}`} colSpan={4}>
					<strong> {t("modules.purchase.field.all.title")}</strong>
				</td>
				<td className={`nowrap nested-cell ${!sameCurrency ? "pb-1" : ""}`}>
					<strong>{FormatMoney(purchase.total_price_net_money)}</strong>
				</td>
				<td className={`nowrap nested-cell ${!sameCurrency ? "pb-1" : ""}`} />
				<td className={`nowrap nested-cell ${!sameCurrency ? "pb-1" : ""}`}>
					<strong>{FormatMoney(purchase.total_tax_price_money)}</strong>
				</td>
				<td
					className={`nowrap nested-cell ${!sameCurrency ? "pb-1" : ""}`}
					colSpan={isActionColumnShown ? 2 : 1}
				>
					<strong>{FormatMoney(purchase.total_price_gross_money)}</strong>
				</td>
			</tr>
			{!sameCurrency && (
				<>
					<tr>
						<td className="nowrap nested-cell pb-1" colSpan={4} />
						<td className="nowrap nested-cell pb-1">
							<strong>
								{purchase.total_price_net_money?.amount !== undefined &&
									exchangeRate !== undefined &&
									FormatMoney({
										amount: exchangeRate * purchase.total_price_net_money.amount,
										currency,
									})}
							</strong>
						</td>
						<td className="nowrap nested-cell pb-1" />
						<td className="nowrap nested-cell pb-1">
							<strong>
								{purchase.total_tax_price_money?.amount !== undefined &&
									exchangeRate !== undefined &&
									FormatMoney({
										amount: exchangeRate * purchase.total_tax_price_money.amount,
										currency,
									})}
							</strong>
						</td>
						<td className="nowrap nested-cell pb-1" colSpan={isActionColumnShown ? 2 : 1}>
							<strong>
								{purchase.total_price_gross_money?.amount !== undefined &&
									exchangeRate !== undefined &&
									FormatMoney({
										amount: exchangeRate * purchase.total_price_gross_money.amount,
										currency,
									})}
							</strong>
						</td>
					</tr>
					<tr>
						<td colSpan={6} className="nowrap nested-cell">
							<strong>
								{t("modules.purchase.field.currency_converter.title")} ({currency})
							</strong>
						</td>
						<td colSpan={isActionColumnShown ? 3 : 2} className="text-end nowrap nested-cell">
							<strong>
								{exchangeRate !== undefined &&
									`1 ${purchase.currency} - ${exchangeRate.toFixed(2)} ${currency}`}
							</strong>
						</td>
					</tr>
				</>
			)}
		</>
	);
};

export default PurchaseItemsCardTableSummary;
